import dayjs from "dayjs";
import type { Insight, InsightGroup, InsightsData } from "../types";

export const formatInsightsDate = (date: string | undefined): string =>
    date ? dayjs(date).format("MMMM D, YYYY") : "";

const UNGROUPED_KEY = "__ungrouped__";
const HIDDEN_POSITION = "whole team";

const isHiddenPosition = (position: string): boolean =>
    position.trim().toLowerCase() === HIDDEN_POSITION;

export const groupInsightsByPosition = (insights: Insight[]): InsightGroup[] => {
    const groups: InsightGroup[] = [];
    const indexByKey = new Map<string, number>();
    for (const insight of insights) {
        const positions = insight.metadata?.positions ?? [];
        if (positions.some(isHiddenPosition)) {
            continue;
        }
        const key = positions.length > 0 ? positions.join(" / ") : UNGROUPED_KEY;
        let index = indexByKey.get(key);
        if (index === undefined) {
            index = groups.length;
            indexByKey.set(key, index);
            groups.push({ key, positions, insights: [] });
        }
        groups[index].insights.push(insight);
    }
    return groups;
};

export const formatInsightPositionLabel = (positions: string[]): string =>
    positions.length > 0 ? positions.join(" / ") : "General";

const formatInsightBlurb = (insight: Insight): string =>
    [insight.observation, insight.evidence, insight.drivers].filter(Boolean).join("\n");

export const formatInsightsAsMarkdown = (data: InsightsData | null | undefined): string => {
    const dateLabel = formatInsightsDate(data?.date);
    const visibleInsights = groupInsightsByPosition(data?.insights ?? []).flatMap(
        (group) => group.insights,
    );
    const blocks = [`# AI Insights${dateLabel ? ` — ${dateLabel}` : ""}`];
    for (const insight of visibleInsights) {
        blocks.push(formatInsightBlurb(insight));
    }
    return blocks.join("\n\n");
};

export const formatInsightGroupAsMarkdown = (
    group: InsightGroup,
    dateLabel?: string,
): string => {
    const positionLabel = formatInsightPositionLabel(group.positions);
    const heading = `# AI Insights — ${positionLabel}${dateLabel ? ` (${dateLabel})` : ""}`;
    const blocks = [heading, ...group.insights.map(formatInsightBlurb)];
    return blocks.join("\n\n");
};
